import {
  ActionIcon,
  Button,
  Group,
  Stack,
  Text,
  Textarea
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Rating } from "../../../../../types/neWebApi.types";
import type { FeedbackState } from "./MessageFeedback.types";

export interface MessageFeedbackProps {
  feedbackState: FeedbackState | undefined;
  messageId: string;
  onSubmit: (messageId: string, rating: Rating, comment?: string) => void;
}

function MessageFeedback({
  feedbackState,
  messageId,
  onSubmit
}: MessageFeedbackProps) {
  const { t } = useTranslation();
  const [commentDraft, setCommentDraft] = useState("");
  const [textareaOpen, { open: openTextarea, close: closeTextarea }] =
    useDisclosure(false);

  const submittedRating = feedbackState?.rating ?? null;
  const hasError = feedbackState?.error ?? false;

  const thumbUpActive = submittedRating === Rating.up && !textareaOpen;
  const thumbDownActive = submittedRating === Rating.down || textareaOpen;

  function handleThumbUp() {
    closeTextarea();
    onSubmit(messageId, Rating.up);
  }

  function handleThumbDown() {
    if (submittedRating === Rating.down) {
      setCommentDraft(feedbackState?.comment ?? "");
    }

    openTextarea();
  }

  function handleCommentSubmit() {
    onSubmit(messageId, Rating.down, commentDraft || undefined);
    closeTextarea();
  }

  function handleCommentCancel() {
    closeTextarea();
    setCommentDraft(feedbackState?.comment ?? "");
  }

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <ActionIcon
          aria-label="Hilfreich"
          aria-pressed={thumbUpActive}
          color={thumbUpActive ? "secondary" : "grey"}
          size="sm"
          variant={thumbUpActive ? "filled" : "subtle"}
          onClick={handleThumbUp}
        >
          <IconThumbUp size={14} />
        </ActionIcon>
        <ActionIcon
          aria-label="Nicht hilfreich"
          aria-pressed={thumbDownActive}
          color={thumbDownActive ? "error" : "grey"}
          size="sm"
          variant={thumbDownActive ? "filled" : "subtle"}
          onClick={handleThumbDown}
        >
          <IconThumbDown size={14} />
        </ActionIcon>
        {hasError && (
          <Text c="error" size="xs">
            {t("errors.AiChat.FeedbackSubmitError")}
          </Text>
        )}
      </Group>
      {textareaOpen && (
        <Stack gap="xs">
          <Textarea
            autosize
            maxLength={2000}
            minRows={2}
            placeholder="Optionaler Kommentar..."
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.currentTarget.value)}
          />
          <Group gap="xs">
            <Button color="primary" size="xs" onClick={handleCommentSubmit}>
              Senden
            </Button>
            <Button size="xs" variant="default" onClick={handleCommentCancel}>
              Abbrechen
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}

export { MessageFeedback };
